// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Contacts
// CRUD for own org's contacts only, orgId auto-set
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '../../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMyContacts } from '../../../hooks/useMyOrgData';
import { useMainStreetStore } from '../../../store/mainstreetStore';
import type { MSContact } from '../../../store/mainstreetStore';
import { formatPhone } from '../../../lib/phoneFormat';

const CONTACT_TYPES = [
  { value: 'primary', label: 'Primary' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'technical', label: 'Technical' },
  { value: 'management', label: 'Management' },
];

const EMPTY: Partial<MSContact> = {
  orgId: '', name: '', title: '', dept: '', type: '',
  email: '', phone: '', altPhone: '', canAuth: 'false', notes: '',
};

export default function PortalContacts() {
  const myOrg = useMyOrg();
  const myContacts = useMyContacts();
  const loadContacts = useMainStreetStore(s => s.loadContacts);
  const loadingContacts = useMainStreetStore(s => s.loadingContacts);
  const createContact = useMainStreetStore(s => s.createContact);
  const updateContact = useMainStreetStore(s => s.updateContact);
  const deleteContact = useMainStreetStore(s => s.deleteContact);

  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MSContact | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MSContact | null>(null);
  const [form, setForm] = useState<Partial<MSContact>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    let list = myContacts;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q),
      );
    }
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [myContacts, search]);

  useEffect(() => { setPage(1); }, [filtered.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key: keyof MSContact, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, orgId: myOrg ? String(myOrg.id) : '' });
    setModalOpen(true);
  };

  const openEdit = (contact: MSContact) => {
    setEditTarget(contact);
    setForm({ ...contact });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'Contact name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateContact(editTarget.id, form);
        notifications.show({ message: 'Contact updated', color: 'var(--color-success)' });
      } else {
        await createContact({ ...form, orgId: myOrg ? String(myOrg.id) : '' });
        notifications.show({ message: 'Contact created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
      setEditTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteContact(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">My Contacts</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingContacts} onClick={() => loadContacts()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Contact</Button>
        </Group>
      </Group>

      <TextInput placeholder="Search name, title, email, phone..."
        leftSection={<IconSearch size={14} />} value={search}
        onChange={e => setSearch(e.target.value)} size="xs" mb="md"
        style={{ maxWidth: 400 }} />

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Title</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Dept</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '8px 16px', textAlign: 'center' }}>Auth</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loadingContacts ? 'Loading...' : 'No contacts found'}
                  </td>
                </tr>
              )}
              {pageRows.map(contact => (
                <tr key={contact.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '8px 16px', fontWeight: 500 }}>{contact.name || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{contact.title || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{contact.dept || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>
                    {contact.type && <Badge size="xs" variant="light">{contact.type}</Badge>}
                  </td>
                  <td style={{ padding: '8px 16px' }}>{contact.email || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{contact.phone ? formatPhone(contact.phone) : '-'}</td>
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <Badge size="xs" color={contact.canAuth === 'true' ? 'var(--color-success)' : '#939598'}>
                      {contact.canAuth === 'true' ? 'Yes' : '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(contact)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(contact)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal — no org selector */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Contact' : 'Add Contact'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Name" required value={form.name ?? ''}
              onChange={e => setField('name', e.target.value)} />
            <TextInput label="Title" value={form.title ?? ''}
              onChange={e => setField('title', e.target.value)} />
            <TextInput label="Department" value={form.dept ?? ''}
              onChange={e => setField('dept', e.target.value)} />
            <Select label="Type" data={CONTACT_TYPES} value={form.type || null}
              onChange={v => setField('type', v ?? '')} clearable />
            <TextInput label="Email" value={form.email ?? ''}
              onChange={e => setField('email', e.target.value)} />
            <TextInput label="Phone" value={form.phone ?? ''}
              onChange={e => setField('phone', e.target.value)} />
            <TextInput label="Alt Phone" value={form.altPhone ?? ''}
              onChange={e => setField('altPhone', e.target.value)} />
          </SimpleGrid>
          <Switch label="Can Authorize" checked={form.canAuth === 'true'}
            onChange={e => setField('canAuth', e.currentTarget.checked ? 'true' : 'false')} />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Contact'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Contact" centered size="sm">
        <Stack>
          <Text size="sm">Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
